import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { number as formatNum, handedness, positionName, styles } from 'yankees-react-utils';
import { useOktaQuery, Advance /* , OutfieldThrow */ } from '../../../data';
import ReportNote from '../../../components/ReportNote';

const queryPlayerOART = async (accessToken, playerId, yearId /* , isCatcher */) => {
    const { positions, bstats, times, reports, scoutinggrades, fstats, errors } = await Advance.playerOart(accessToken, playerId, yearId);
    // const throws = isCatcher ? [] : await OutfieldThrow.query(accessToken, { playerId, sinceGamedate: new Date(`1/1/${yearId - 1}`) });
    return {
        positions,
        reports,
        bStats: bstats.length ? bstats[0] : null,
        scoutingGrade: scoutinggrades.length ? scoutinggrades[0] : null,
        times: times.length ? times[0] : null,
        fStats: fstats.find(({ position }) => position === 2),
        errors: positions.reduce((acc, { position }) => ({
            ...acc,
            [position]: errors.filter((e) => e.position === position).sort((a, b) => b.yearid - a.yearid),
        }), {}),
        // hasOutfieldThrows: !!throws?.length,
    };
};

const toolGradeCssClass = (grade, min, max) => {
    let value = grade;
    if (min && max) value = (grade <= min || grade >= max) ? grade : null;

    let gradeClass = '';
    if (value) {
        const thresholds = [76, 72, 68, 63.5, 60.5, 57.5, 55.5, 53.5, 51.8, 49, 45.8, 42, 37, 32, 27, 19];
        const gradeIdx = thresholds.findIndex((threshold) => value > threshold);
        if (gradeIdx >= 0) gradeClass = `toolgrade_${gradeIdx + 1}`;
    }
    return gradeClass;
};

const StyledThrowingArmsRow = styled.tr`
    &&&& {
        > th, > td {
            border: 1px solid ${styles.theme.border.bombay};
            &:not(.oart-expand) {
                height: 1px;
            }
            &.thickborder-right {
                border-right-color: ${styles.theme.fontColors.mineShaft};
                border-right-width: 2px;
            }
            &.thickborder-bottom {
                border-bottom-color: ${styles.theme.fontColors.mineShaft};
                border-bottom-width: 2px;
            }
            table {
                td {
                    vertical-align: top;
                    white-space: nowrap;
                }
                tr {
                    background-color: transparent;
                }
            }
        }
        &:last-of-type, &:nth-last-of-type(2) {
            > th, > td {
                &.thickborder-bottom {
                    border-bottom-color: ${styles.theme.border.bombay};
                    border-bottom-width: 1px;
                }
            }
        }
    }
    .bats-right { color: #000000; }
    .bats-left { color: #CC0000; }
    .bats-switch { color:#0000CC; }
    .toolgrade_1 { background-color: rgb(0,68,0); color: #fff; }
    .toolgrade_2 { background-color: rgb(0,90,0); color: #fff; }
    .toolgrade_3 { background-color: rgb(0,102,0); color: #fff; }
    .toolgrade_4 { background-color: rgb(0,114,0); color: #fff; }
    .toolgrade_5 { background-color: rgb(0,128,0); color: #fff; }
    .toolgrade_6 { background-color: rgb(0,138,0); color: #fff; }
    .toolgrade_7 { background-color: rgb(90,195,0); color: #fff; }
    .toolgrade_8 { background-color: rgb(150,225,0); }
    .toolgrade_9 { background-color: rgb(190,240,0); }
    .toolgrade_10 { background-color: rgb(255,255,0); }
    .toolgrade_11 { background-color: rgb(225,225,0); }
    .toolgrade_12 { background-color: rgb(255,191,24); }
    .toolgrade_13 { background-color: rgb(255,150,10); }
    .toolgrade_14 { background-color: rgb(255,90,5); color: #fff; }
    .toolgrade_15 { background-color: rgb(255,66,30); color: #fff; }
    .toolgrade_16 { background-color: rgb(255,0,0); color: #fff; }
`;

const ThrowingArms = ({ player, position, year, levelId }) => {
    const { t: translate } = useTranslation();
    const isCatcher = position === 'C';
    const { data, isLoading } = useOktaQuery(['player-oart', player.PlayerID, year], (t) => queryPlayerOART(t, player.PlayerID, year, isCatcher));

    if (!data || isLoading) return null;

    const { positions, errors, scoutingGrade, times, bStats, fStats } = data;
    const currentMonth = new Date().getMonth() + 1;
    return (
        <>
            <StyledThrowingArmsRow>
                <td>
                    <b className={`text-nowrap bats-${handedness(player.Bats, true).toLowerCase()}`}>{player.FormalName}</b>
                </td>
                {isCatcher && <th>{translate('sections.opponent.team.overall-arm')}</th>}
                {isCatcher && <th>{translate('sections.opponent.team.abbr-exchange')}</th>}
                <th>{translate('sections.opponent.team.arm-str')}</th>
                <th>{translate('sections.opponent.team.arm-acc')}</th>
                {isCatcher && <th>{translate('sections.opponent.team.abbr-block')}</th>}
                {!isCatcher && <th>{translate('sections.opponent.team.abbr-field')}</th>}
                <th>{translate('sections.opponent.team.run-spd')}</th>
                <th className="thickborder-right">{translate('sections.opponent.team.abbr-home-gtr-1b')}</th>

                <th>{translate('baseball.abbr-stolen-base')} {translate('baseball.2b')}</th>
                <th>{translate('baseball.abbr-stolen-base')} {translate('baseball.3b')}</th>
                <th>{translate('baseball.2b')} {translate('sections.opponent.team.abbr-movement')}</th>
                <th>{translate('baseball.3b')} {translate('sections.opponent.team.abbr-movement')}</th>

                {isCatcher && <th>{translate('baseball.abbr-caught-stealing')}{translate('baseball.3b')}</th>}
                {isCatcher && <th>{translate('baseball.abr-pickoff')}</th>}
                {isCatcher && <th>{translate('sections.opponent.team.time')} {translate('baseball.2b')}</th>}
            </StyledThrowingArmsRow>
            <StyledThrowingArmsRow>
                <td rowSpan={2} className="thickborder-bottom">
                    {!positions.length && <div>{positionName(player.PrimaryPosition, translate)}</div>}
                    {!!positions.length && (
                        <table>
                            <tbody>
                                {positions.map((rec) => (
                                    <tr key={rec.position}>
                                        <td>{positionName(rec.position, translate)}:</td>
                                        <td>
                                            {errors[rec.position].map(({ yearid, fe, te }) => (
                                                <div key={yearid}>{currentMonth < 5 ? `${yearid}: ` : ''}{fe}/{te}</div>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </td>
                {isCatcher && <td className={toolGradeCssClass(scoutingGrade?.parm, 40, 60)}>{scoutingGrade?.parm}</td>}
                {isCatcher && <td className={toolGradeCssClass(scoutingGrade?.pex, 40, 60)}>{scoutingGrade?.pex}</td>}
                <td className={toolGradeCssClass(scoutingGrade?.pas, 40, 60)}>{scoutingGrade?.pas}</td>
                <td className={toolGradeCssClass(scoutingGrade?.paa, 40, 60)}>{scoutingGrade?.paa}</td>
                {isCatcher && <td className={toolGradeCssClass(scoutingGrade?.pbl, 40, 60)}>{scoutingGrade?.pbl}</td>}
                {!isCatcher && <td className={toolGradeCssClass(scoutingGrade?.pfa, 40, 60)}>{scoutingGrade?.pfa}</td>}
                <td className={toolGradeCssClass(scoutingGrade?.prs, 35, 65)}>{scoutingGrade?.prs}</td>
                <td className="thickborder-right">
                    {times?.hto1bcntlhh > 0 && !!times?.hto1btimelhh && (
                        <span className="bats-left">{formatNum(times.hto1btimelhh, 2)}</span>
                    )}
                    {times?.hto1bcntlhh > 0 && !!times?.hto1btimelhh && times?.hto1bcnrhh > 0 && !!times?.hto1btimerhh ? '/' : ''}
                    {times?.hto1bcntrhh > 0 && !!times?.hto1btimerhh && (
                        <span className="bats-right">{formatNum(times.hto1btimerhh, 2)}</span>
                    )}
                </td>
                {!bStats && <td colSpan={4} />}
                {bStats && (
                    <>
                        <td>{bStats.sb2b}/{bStats.sb2b + bStats.cs2b}</td>
                        <td>{bStats.sb3b}/{bStats.sb3b + bStats.cs3b}</td>
                        <td>{formatNum(bStats.move2btime, 2)}</td>
                        <td>{formatNum(bStats.move3btime, 2)}</td>
                    </>
                )}
                {isCatcher && !fStats && <td colSpan={3} />}
                {isCatcher && fStats && (
                    <>
                        <td>{fStats.ccs3b}/{fStats.ccs3b + fStats.sb3b}</td>
                        <td>{fStats.pko}/{fStats.pkoatt}</td>
                        {!fStats.time2b && <td />}
                        {fStats.time2b && (
                            <td>
                                {formatNum(fStats.time2b10pct, 2)}-{formatNum(fStats.time2b90pct, 2)} ({formatNum(fStats.time2b, 2)})
                            </td>
                        )}
                    </>
                )}
            </StyledThrowingArmsRow>
            <StyledThrowingArmsRow>
                <td colSpan={isCatcher ? 14 : 9} className="oart-expand thickborder-bottom">
                    <ReportNote playerId={player.PlayerID} type="oart" section={1} levelId={levelId} />
                </td>
            </StyledThrowingArmsRow>
        </>
    );
};

ThrowingArms.propTypes = {
    year: PropTypes.number.isRequired,
    player: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FormalName: PropTypes.string.isRequired,
        PrimaryPosition: PropTypes.number.isRequired,
        Bats: PropTypes.number.isRequired,
    }).isRequired,
    position: PropTypes.string,
    levelId: PropTypes.number,
};
ThrowingArms.defaultProps = {
    position: null,
    levelId: 8,
};

export default ThrowingArms;
